/*  Shared  */
import { PageNotFound } from 'application/Shared/Components/PageNotFound';
import { SessionHelper } from 'application/Shared/Helpers';
import { debug } from 'application/Shared/Utils';
import { useSession } from 'hooks/redux/Selectors/session';

/*  React  */
import React from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router';

export function checkPermissions(sessionHelper, requiredPermissions) {
    // If there is no session, the user obviously doesn't have permissions to view the route.
    if (!sessionHelper.exists()) return false;

    // Check each set of permissions to determine if the have access or not.
    let hasPermission = true;
    for (let i = 0; i < requiredPermissions.length; i += 1) {
        const perm = requiredPermissions[i];
        const check = sessionHelper.hasPermission(perm.required, perm.operator);
        if (!check) {
            hasPermission = false;
            break;
        }
    }

    return hasPermission;
}

/**
 * PermissionCheck is used to restrict access to certain routes based on user having a session
 * and the required permissions.
 * @param {Object[]} requiredPermissions - an array of objects of the necessary permissions required to view the specified route.
 *  Multiple permission objects can be provided and user must have access to each level.
 * @example
 * [
 *     {
 *         required: ['system_admin', 'reporting_organizational'],
 *         operator: 'and'
 *     },
 *     {
 *         required: ['reporting_organizational', 'reporting_personal', 'system_admin'],
 *         operator: 'or'
 *     }
 * ]
 * @param {Object} session - a user session
 * @param {JSX.Element} Component - The component to render if the user has the required permissions.
 * @param {Function} render - The component render function to call if the user has the required permissions.
 *
 * @returns {JSX.Element}
 */
export const PermissionCheck = ({
    requiredPermissions = [],
    Component,
    render: routeRenderFn
}) => {
    const session = useSession();

    const location = useLocation();
    const params = useParams();
    const navigate = useNavigate();

    const { search } = location;

    // If the user doesn't have a session, redirect them to logout, which will do any necessary cleanup
    // and then redirect them to login.
    if (!session) {
        return <Navigate to={{ pathname: '/logout', search }} replace />;
    }

    const sessionHelper = new SessionHelper(session);
    const hasPermission = checkPermissions(sessionHelper, requiredPermissions);
    if (!hasPermission) {
        debug.w('Insufficient Permission', requiredPermissions);
    }

    if (hasPermission) {
        const props = { location, match: { params }, navigate };
        if (routeRenderFn) {
            return routeRenderFn(props);
        }

        return <Component {...props} />;
    }

    return <PageNotFound />;
};
